import { Box, Drawer, Hidden } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AppsIcon from '@mui/icons-material/Apps';
import CreateIcon from '@mui/icons-material/Create';
import DevicesIcon from '@mui/icons-material/Devices';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TuneIcon from '@mui/icons-material/Tune';
import SimCardIcon from '@mui/icons-material/SimCard';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { CompanyLogoHorizontalWithTitle, MDMLogoHorizontalWithTitle } from 'src/components/logo';
import ChartSquareBarIcon from '../../../icons/ChartSquareBar';
import DeviceTabletIcon from '../../../icons/DeviceTablet';
import NavSection from '../../NavSection';
import Scrollbar from '../../Scrollbar';
import useSettings from '../../../hooks/useSettings';

// Resource-driven menu — each top-level item links straight to a resource list.
// Create / Update / Upload / Bulk-* actions live on the list page itself
// (right-aligned action buttons), which keeps the sidebar focused on
// "where can I go?" and removes the implementation-flavoured CRUD vocabulary.
//
// Sub-grouping is reserved for genuinely distinct resource families
// (Banks → Bank Device Records, Incidents → Incident Types, etc.).
const sections = [
  {
    title: 'General',
    items: [
      {
        title: 'Overview',
        path: '/dashboard',
        icon: <ChartSquareBarIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'Management',
    items: [
      {
        title: 'Devices',
        path: '/dashboard/devices',
        icon: <DeviceTabletIcon fontSize="small" />,
        permissions: ['commonlib.view_device'],
      },
      {
        title: 'Companies',
        path: '/dashboard/companies',
        icon: <LocationCityIcon fontSize="small" />,
        permissions: ['commonlib.view_company'],
      },
      {
        title: 'Merchants',
        path: '/dashboard/merchants',
        icon: <PointOfSaleIcon fontSize="small" />,
        permissions: ['commonlib.view_company'],
      },
      {
        title: 'Applications',
        path: '/dashboard/apps',
        icon: <AppsIcon fontSize="small" />,
        permissions: ['commonlib.view_application'],
      },
      {
        title: 'Repositories',
        path: '/dashboard/repositories',
        icon: <AppRegistrationIcon fontSize="small" />,
        permissions: ['commonlib.view_repository'],
      },
      {
        title: 'Parameters',
        path: '/dashboard/parameters',
        icon: <TuneIcon fontSize="small" />,
        permissions: ['commonlib.view_parameter'],
      },
      {
        title: 'Tags',
        path: '/dashboard/tags',
        icon: <LocalOfferIcon fontSize="small" />,
        permissions: ['commonlib.view_tag'],
      },
      {
        title: 'Sims',
        path: '/dashboard/sim',
        icon: <SimCardIcon fontSize="small" />,
        permissions: ['commonlib.view_simstock'],
      },
      {
        title: 'Banks',
        path: '/dashboard/banks',
        icon: <AccountBalanceIcon fontSize="small" />,
        children: [
          {
            title: 'Banks',
            path: '/dashboard/banks',
            permissions: ['commonlib.view_bank'],
          },
          {
            title: 'Bank Device Records',
            path: '/dashboard/bankdevices',
            permissions: ['commonlib.view_bankdevice', 'commonlib.view_bankdeviceregistry'],
          },
        ],
      },
      {
        title: 'Incidents',
        path: '/dashboard/incidents',
        icon: <SummarizeIcon fontSize="small" />,
        children: [
          {
            title: 'Incidents',
            path: '/dashboard/incidents',
            permissions: ['commonlib.view_incident'],
          },
          {
            title: 'Incident Types',
            path: '/dashboard/incidenttype',
            permissions: ['commonlib.view_incidenttype'],
          },
        ],
      },
      {
        title: 'SLA Times',
        path: '/dashboard/companySLAtime',
        icon: <TimelapseIcon fontSize="small" />,
        permissions: ['commonlib.view_companyslatime'],
      },
      {
        title: 'Reports',
        path: '/dashboard/reports',
        icon: <AssessmentIcon fontSize="small" />,
        children: [
          {
            title: 'Incident Report',
            path: '/dashboard/reports',
            permissions: ['commonlib.view_incident'],
          },
          {
            title: 'SLA Report',
            path: '/dashboard/reports/sla-reports',
            permissions: ['commonlib.view_incident'],
          },
          {
            title: 'Invoicing Report',
            path: '/dashboard/reports/invoicing-reports',
            permissions: ['commonlib.view_incident'],
          },
        ],
      },
      {
        title: 'Model Types',
        path: '/dashboard/modeltypes',
        icon: <DevicesIcon fontSize="small" />,
        permissions: ['commonlib.view_modeltype'],
      },
      {
        title: 'Logs',
        path: '/dashboard/logs',
        icon: <CreateIcon fontSize="small" />,
        children: [
          {
            title: 'Audit Logs',
            path: '/dashboard/logs/auditlog',
            permissions: ['commonlib.view_auditlog'],
          },
          {
            title: 'Device Application Logs',
            path: '/dashboard/logs/deviceapplicationlog',
            permissions: ['commonlib.view_deviceapplicationlog'],
          },
          {
            title: 'Device Parameter Logs',
            path: '/dashboard/logs/deviceparameterlog',
            permissions: ['commonlib.view_deviceparameterlog'],
          },
          {
            title: 'User Logs',
            path: '/dashboard/logs/userlog',
            permissions: ['commonlib.view_userlog'],
          },
          {
            title: 'Sync Logs',
            path: '/dashboard/logs/sync',
            permissions: ['commonlib.view_synclog'],
          },
          {
            title: 'API Logs',
            path: '/dashboard/logs/api',
            permissions: ['commonlib.view_apilog'],
          },
        ],
      },
      {
        title: 'Users',
        path: '/dashboard/users',
        icon: <ManageAccountsIcon fontSize="small" />,
        permissions: ['commonlib.view_user'],
      },
      {
        title: 'Roles',
        path: '/dashboard/roles',
        icon: <GroupsIcon fontSize="small" />,
        permissions: ['auth.view_group'],
      },
      {
        title: 'System Jobs',
        path: '/dashboard/system/jobs',
        icon: <ScheduleIcon fontSize="small" />,
        permissions: ['commonlib.manage_celery_jobs'],
      },
    ],
  },
];

const DashboardSidebar = (props) => {
  const { onMobileClose, openMobile } = props;
  const location = useLocation();
  const theme = useTheme();
  const { settings } = useSettings();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Scrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <RouterLink to="/" style={{ textDecoration: 'none' }}>
              {settings.companyName !== 'TOKEN MDM' ? (
                <CompanyLogoHorizontalWithTitle
                  color={settings.theme === 'LIGHT' ? settings.themeColor : '#ffffff'}
                  companyName={settings.companyName}
                  logoUrl={settings.logo}
                  sx={{ mt: 1, height: 50, width: 240 }}
                />
              ) : (
                <MDMLogoHorizontalWithTitle
                  color={theme.palette.primary.contrastText}
                  sx={{ mt: 1, height: 50, width: 240 }}
                />
              )}
            </RouterLink>
          </Box>
        </Hidden>
        <Box sx={{ p: 2 }}>
          {sections.map((section) => (
            <NavSection
              key={section.title}
              pathname={location.pathname}
              sx={{ '& + &': { mt: 3 } }}
              {...section}
            />
          ))}
        </Box>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              width: 280,
            },
          }}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          anchor="left"
          open
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              height: 'calc(100% - 64px) !important',
              top: '64px !Important',
              width: 280,
            },
          }}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
